// time constants (ms)
export const SAVE_TO_LOCAL_STORAGE_TIMEOUT = 300;
export const INITIAL_SCENE_UPDATE_TIMEOUT = 5000;
export const FILE_UPLOAD_TIMEOUT = 300;
export const LOAD_IMAGES_TIMEOUT = 500;
export const COLLAB_FILE_REQUEST_TIMEOUT_MS = 5000;
export const COLLAB_FILE_BUFFER_WAIT_TIMEOUT_MS = 5000;
export const COLLAB_FILE_BUFFER_POLL_INTERVAL_MS = 50;
// One encrypted relay frame for a 2 MiB image is about 3.6 MiB after the data
// URL and relay base64 layers. Waiting above 4 MiB keeps the browser's pending
// file backlog near one image while scene and cursor messages use their own path.
export const COLLAB_FILE_BUFFERED_AMOUNT_LIMIT_BYTES = 4 * 1024 * 1024;
export const SYNC_FULL_SCENE_INTERVAL_MS = 20000;
export const SYNC_BROWSER_TABS_TIMEOUT = 50;
// How often this client may send its own pointer position. Cloudflare bills an
// inbound WebSocket message at 20:1, and cursor messages are almost all of the
// collab Worker's daily request budget, so this number decides how many hours a
// day the room can stay open: a five peer room costs roughly 27,000 billable
// requests an hour at 33 ms and roughly 9,000 at 100 ms, against a budget of
// 70,000 (`DAILY_REQUEST_BUDGET` in `workers/collab/wrangler.jsonc`). 100 ms
// also finally matches the Worker's `CURSOR_HZ` planning figure of 10.
//
// The stutter this would otherwise cause is undone on the receiving side, see
// `CURSOR_INTERPOLATION_DURATION_MS`.
export const CURSOR_SYNC_TIMEOUT = 100; // 10 Hz, matches the Worker's CURSOR_HZ
// How long a remote cursor takes to travel from where it is currently drawn to
// the position the newest message reported (`collab/RemoteCursorInterpolator.ts`).
//
// Matching `CURSOR_SYNC_TIMEOUT` is what makes the motion smooth rather than
// merely continuous: one send interval of distance is spread over exactly one
// send interval of time, so a peer moving at a constant speed is drawn at a
// constant speed instead of easing in and out ten times a second. The price is
// that the cursor is drawn one interval in the past, which is also the worst
// case: interpolation can never add more delay than this number. Set it to 0 to
// draw remote cursors exactly as they arrive.
export const CURSOR_INTERPOLATION_DURATION_MS = CURSOR_SYNC_TIMEOUT;
export const DELETED_ELEMENT_TIMEOUT = 24 * 60 * 60 * 1000; // 1 day

// should be aligned with MAX_ALLOWED_FILE_BYTES
export const FILE_UPLOAD_MAX_BYTES = 4 * 1024 * 1024; // 4 MiB
// 1 year (https://stackoverflow.com/a/25201898/927631)
export const FILE_CACHE_MAX_AGE_SEC = 31536000;

// HTTP paths served by the collab Worker (`workers/collab/src/worker.ts`).
export const COLLAB_AVAILABILITY_PATH = "/collab/availability";
export const COLLAB_SESSION_PATH = "/collab/session";
// Distinct from the path above on purpose: opening a room reserves budget,
// joining one only borrows the terms of a room that is already paid for.
export const COLLAB_JOIN_PATH = "/collab/join";
export const COLLAB_ROOM_PATH_PREFIX = "/collab/room";

// Bounded exponential backoff for the room WebSocket, which (unlike
// socket.io) has no built-in reconnect. Delay doubles per attempt up to the
// max, and reconnecting stops for good after the max attempt count.
export const COLLAB_RECONNECT_BASE_DELAY_MS = 1000;
export const COLLAB_RECONNECT_MAX_DELAY_MS = 30000;
export const COLLAB_RECONNECT_MAX_ATTEMPTS = 5;

export enum WS_SUBTYPES {
  INVALID_RESPONSE = "INVALID_RESPONSE",
  INIT = "SCENE_INIT",
  UPDATE = "SCENE_UPDATE",
  MOUSE_LOCATION = "MOUSE_LOCATION",
  IDLE_STATUS = "IDLE_STATUS",
  USER_VISIBLE_SCENE_BOUNDS = "USER_VISIBLE_SCENE_BOUNDS",
  FILE_PUSH = "FILE_PUSH",
  FILE_REQUEST = "FILE_REQUEST",
  FILE_RESPONSE = "FILE_RESPONSE",
}

export const ROOM_ID_BYTES = 10;

export const STORAGE_KEYS = {
  LOCAL_STORAGE_ELEMENTS: "drawOla",
  LOCAL_STORAGE_APP_STATE: "drawOla-state",
  LOCAL_STORAGE_COLLAB: "drawOla-collab",
  LOCAL_STORAGE_THEME: "drawOla-theme",
  LOCAL_STORAGE_DEBUG: "drawOla-debug",
  VERSION_DATA_STATE: "version-dataState",
  VERSION_FILES: "version-files",

  IDB_LIBRARY: "drawOla-library",
  IDB_TTD_CHATS: "excalidraw-ttd-chats",

  // do not use apart from migrations
  __LEGACY_LOCAL_STORAGE_LIBRARY: "drawOla-library",
} as const;

export const COOKIES = {
  AUTH_STATE_COOKIE: "drawOlaplus-auth",
} as const;

export const isDrawolaPlusSignedUser = document.cookie.includes(
  COOKIES.AUTH_STATE_COOKIE,
);
